import { clsx } from 'clsx';
import { useMasterDocumentFormatters } from './formatters';

// ─── Types ─────────────────────────────────────────────────

export interface CurrencyPanelProps {
  /** Código de moneda, ej: "USD", "EUR", "COP" */
  code: string;
  /** Valor de la tasa de cambio */
  rate?: string | number;
  /** Etiqueta antes de la tasa. Default: "TRM" */
  rateLabel?: string;
  /** Handler para abrir modal de cambio de moneda */
  onClick?: () => void;
  className?: string;
}

// ─── CurrencyPanel ─────────────────────────────────────────

/**
 * Indicador de moneda y tasa de cambio (TRM) para la franja de contexto.
 *
 * Muestra el código de moneda y, si se proporciona la tasa, la formatea
 * con 2 decimales siguiendo el idioma activo (`useMasterDocumentFormatters`).
 * Cuando se pasa `onClick` actúa como botón interactivo para abrir un
 * selector de moneda.
 *
 * @example
 * ```tsx
 * <CurrencyPanel code="USD" rate={4185.5} rateLabel="TRM" onClick={() => setOpen(true)} />
 * // → "USD · TRM 4.185,50"
 * ```
 */
export function CurrencyPanel({
  code,
  rate,
  rateLabel = 'TRM',
  onClick,
  className,
}: CurrencyPanelProps) {
  const { formatRate } = useMasterDocumentFormatters();
  if (!code) return null;

  const formattedRate =
    rate != null
      ? typeof rate === 'number'
        ? formatRate(rate)
        : rate
      : null;

  const display = formattedRate ? `${code} · ${rateLabel} ${formattedRate}` : code;

  const baseClass = clsx(
    'inline-flex items-center gap-1.5',
    'text-[12px] font-semibold',
    'text-primary-600 dark:text-primary-400',
    'border border-[#E0E0E0] dark:border-dark-border-primary',
    'rounded-[7px] px-[10px] py-[4px]',
    'bg-transparent',
    onClick && [
      'cursor-pointer',
      'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
      'transition-colors duration-150',
      'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
      'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
    ],
    className,
  );

  const icon = (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={baseClass}>
        {icon}
        {display}
      </button>
    );
  }

  return (
    <span className={baseClass}>
      {icon}
      {display}
    </span>
  );
}
